import React from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument } from '@popety-io/types'
import { Icon } from '../../../../atoms/Icon'
import { Text } from '../../../../atoms/Text'

const DocumentItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  transition: background-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`

const DocumentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const DocumentTitle = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DocumentMeta = styled(Text)`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing.micro};
`

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.background.tertiary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ImportantBadge = styled.span`
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.warning}20;
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  margin-left: ${({ theme }) => theme.spacing.xsmall};
`

export interface DocumentItemTranslations {
  documentTypes?: {
    rule?: string
    map?: string
  }
  important?: string
  download?: string
}

const DEFAULT_TRANSLATIONS: DocumentItemTranslations = {
  documentTypes: {
    rule: 'rule',
    map: 'map',
  },
  important: 'Important',
  download: 'Download',
}

interface DocumentItemProps {
  document: LandUsePlanDocument
  translations?: DocumentItemTranslations
  locale?: string
}

const getDocumentIcon = (docType?: string): string => {
  switch (docType) {
    case 'rule':
      return 'file-text'
    case 'map':
      return 'map'
    default:
      return 'file'
  }
}

const formatDate = (dateString?: string, locale?: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale || 'fr-CH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

export const DocumentItem: React.FC<DocumentItemProps> = ({
  document,
  translations,
  locale,
}) => {
  const t = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    documentTypes: {
      ...DEFAULT_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
  }

  const handleDownload = () => {
    // Open document URL in new tab
    if (document.url) {
      window.open(document.url, '_blank', 'noopener,noreferrer')
    }
  }

  // Translate document type
  const getTranslatedDocumentType = (docType?: string): string => {
    if (!docType) return ''
    return t.documentTypes?.[docType as keyof typeof t.documentTypes] || docType
  }

  const metaParts = []
  if (document.document_type) {
    metaParts.push(getTranslatedDocumentType(document.document_type))
  }
  if (document.publication_date) {
    metaParts.push(formatDate(document.publication_date, locale))
  }
  if (document.language) {
    metaParts.push(document.language.toUpperCase())
  }

  const iconName = getDocumentIcon(document.document_type) as any

  return (
    <DocumentItemContainer>
      <DocumentIcon>
        <Icon name={iconName} size="small" />
      </DocumentIcon>

      <DocumentInfo>
        <DocumentTitle>
          {document.title}
          {document.important && <ImportantBadge>{t.important}</ImportantBadge>}
        </DocumentTitle>
        {metaParts.length > 0 && (
          <DocumentMeta>{metaParts.join(' • ')}</DocumentMeta>
        )}
      </DocumentInfo>

      <DownloadButton onClick={handleDownload} type="button">
        <Icon name="download" size="small" />
        {t.download}
      </DownloadButton>
    </DocumentItemContainer>
  )
}
